import {
    mxClient,
    mxClipboard,
    mxEdgeHandler,
    mxEvent,
    mxEventObject,
    mxEventSource,
    mxResources,
    mxUtils
} from './mxGraphFactory';
import {Editor} from './Editor';

export const Actions = function (editorUi) {
    this.editorUi = editorUi;
    this.actions = {};
    this.init();
};

Actions.prototype.init = function () {
    const ui = this.editorUi;
    const editor = ui.editor;
    const graph = editor.graph;
    const isEnabled = graph.editable;

    this.addAction('save', () => ui.save(), isEnabled, Editor.ctrlKey + '+S');
    this.addAction('validate', () => ui.validate(), isEnabled, Editor.ctrlKey + '+Shift+V');
    this.addAction('undo', () => ui.undo(), isEnabled, Editor.ctrlKey + '+Z');
    this.addAction('redo', () => ui.redo(), isEnabled, (!mxClient.IS_WIN) ? Editor.ctrlKey + '+Shift+Z' : Editor.ctrlKey + '+Y');
    this.addAction('cut', () => {
        if (graph.getSelectionCell() &&
            graph.getSelectionCell().vertex &&
            !graph.getSelectionCell().isStart() &&
            !graph.getSelectionCell().isMap()) {
            mxClipboard.cut(graph)
        }
    }, isEnabled, Editor.ctrlKey + '+X');
    this.addAction('copy', () => {
        if (graph.getSelectionCell() &&
            graph.getSelectionCell().vertex &&
            !graph.getSelectionCell().isStart() &&
            !graph.getSelectionCell().isMap()) {
            mxClipboard.copy(graph);
        }
    }, isEnabled, Editor.ctrlKey + '+C');
    this.addAction('paste', () => mxClipboard.paste(graph), isEnabled && !graph.isCellLocked(graph.getDefaultParent()), Editor.ctrlKey + '+V');
    this.addAction('pasteHere', () => {
        graph.getModel().beginUpdate();
        try {
            const cells = mxClipboard.paste(graph);

            if (cells != null) {
                let includeEdges = true;

                for (let i = 0; i < cells.length && includeEdges; i++) {
                    includeEdges = includeEdges && graph.model.isEdge(cells[i]);
                }

                const t = graph.view.translate;
                const s = graph.view.scale;
                const dx = t.x;
                const dy = t.y;
                let bb = null;

                if (cells.length === 1 && includeEdges) {
                    const geo = graph.getCellGeometry(cells[0]);

                    if (geo != null) {
                        bb = geo.getTerminalPoint(true);
                    }
                }

                bb = (bb != null) ? bb : graph.getBoundingBoxFromGeometry(cells, includeEdges);

                if (bb != null) {
                    const x = Math.round(graph.snap(graph.popupMenuHandler.triggerX / s - dx));
                    const y = Math.round(graph.snap(graph.popupMenuHandler.triggerY / s - dy));

                    graph.cellsMoved(cells, x - bb.x, y - bb.y);
                }
            }
        } finally {
            graph.getModel().endUpdate();
        }
    }, isEnabled && !graph.isCellLocked(graph.getDefaultParent()));

    const deleteCells = (includeEdges) => {
        graph.escape();
        const cells = graph.getDeletableCells(graph.getSelectionCells());

        if (cells != null && cells.length > 0) {
            const parents = graph.model.getParents(cells);
            graph.removeCells(cells, includeEdges);

            if (parents != null) {
                const select = [];

                for (let i = 0; i < parents.length; i++) {
                    if (graph.model.contains(parents[i]) && (graph.model.isVertex(parents[i]) || graph.model.isEdge(parents[i]))) {
                        select.push(parents[i]);
                    }
                }

                graph.setSelectionCells(select);
            }
        }
    }

    this.addAction('delete', () => deleteCells(true), isEnabled, 'Delete');
    this.addAction('duplicate', () => {
          if (graph.getSelectionCell() &&
              graph.getSelectionCell().vertex &&
              !graph.getSelectionCell().isStart() &&
              !graph.getSelectionCell().isMap()) {
            graph.setSelectionCells(graph.duplicateCells());
          }
        }, isEnabled, Editor.ctrlKey + '+D');
    this.addAction('selectVertices', () => graph.selectVertices(), isEnabled, Editor.ctrlKey + '+Shift+I');
    this.addAction('selectEdges', () => graph.selectEdges(), isEnabled, Editor.ctrlKey + '+Shift+E');
    this.addAction('selectAll', () => graph.selectAll(null, true), isEnabled, Editor.ctrlKey + '+A');
    this.addAction('selectNone', () => graph.clearSelection(), isEnabled, Editor.ctrlKey + '+Shift+A');

    this.addAction('home', () => graph.home(), null, 'Home');

    this.addAction('edit', () => graph.startEditingAtCell(), isEnabled, 'F2/Enter');
    this.addAction('resetView', () => {
        graph.zoomTo(1);
        ui.resetGraphView();
    }, null, Editor.ctrlKey + '+H');
    this.addAction('zoomIn', () => graph.zoomIn(), null, Editor.ctrlKey + ' + (Numpad) / Alt+Mousewheel');
    this.addAction('zoomOut', () => graph.zoomOut(), null, Editor.ctrlKey + ' - (Numpad) / Alt+Mousewheel');
    this.addAction('addWaypoint', () => {
        const cell = graph.getSelectionCell();

        if (cell != null && graph.getModel().isEdge(cell)) {
            const handler = editor.graph.selectionCellsHandler.getHandler(cell);

            if (handler instanceof mxEdgeHandler) {
                const t = graph.view.translate;
                const s = graph.view.scale;
                let dx = t.x;
                let dy = t.y;

                let parent = graph.getModel().getParent(cell);
                let parentGeometry = graph.getCellGeometry(parent);

                while (graph.getModel().isVertex(parent) && parentGeometry != null) {
                    dx += parentGeometry.x;
                    dy += parentGeometry.y;

                    parent = graph.getModel().getParent(parent);
                    parentGeometry = graph.getCellGeometry(parent);
                }

                const x = Math.round(graph.snap(graph.popupMenuHandler.triggerX / s - dx));
                const y = Math.round(graph.snap(graph.popupMenuHandler.triggerY / s - dy));

                handler.addPointAt(handler.state, x, y);
            }
        }
    });
    this.addAction('removeWaypoint', () => {
        const rmWaypointAction = ui.actions.get('removeWaypoint');

        if (rmWaypointAction.handler != null) {
            rmWaypointAction.handler.removePoint(rmWaypointAction.handler.state, rmWaypointAction.index);
        }
    });
    this.addAction('clearWaypoints', () => {
        let cells = graph.getSelectionCells();

        if (cells != null) {
            cells = graph.addAllEdges(cells);

            graph.getModel().beginUpdate();
            try {
                for (let i = 0; i < cells.length; i++) {
                    let cell = cells[i];

                    if (graph.getModel().isEdge(cell)) {
                        let geo = graph.getCellGeometry(cell);

                        if (geo != null) {
                            geo = geo.clone();
                            geo.points = null;
                            graph.getModel().setGeometry(cell, geo);
                        }
                    }
                }
            } finally {
                graph.getModel().endUpdate();
            }
        }
    }, null, 'Alt+Shift+C');
};

/**
 * Registers the given action under the given name.
 */
Actions.prototype.addAction = function (key, funct, enabled, shortcut) {
    let title;

    if (key.substring(key.length - 3) === '...') {
        key = key.substring(0, key.length - 3);
        title = mxResources.get(key) + '...';
    } else {
        title = mxResources.get(key);
    }

    return this.put(key, new Action(title, funct, enabled, shortcut));
};

/**
 * Registers the given action under the given name.
 */
Actions.prototype.put = function (name, action) {
    this.actions[name] = action;
    return action;
};

/**
 * Returns the action for the given name or null if no such action exists.
 */
Actions.prototype.get = function (name) {
    return this.actions[name];
};

/**
 * Constructs a new action for the given parameters.
 */
export const Action = function (label, funct, enabled, shortcut) {
    mxEventSource.call(this);
    this.label = label;
    this.funct = funct;
    this.enabled = enabled || true;
    this.shortcut = shortcut;
    this.visible = true;
};

// Action inherits from mxEventSource
mxUtils.extend(Action, mxEventSource);

/**
 * Sets the enabled state of the action and fires a stateChanged event.
 */
Action.prototype.setEnabled = function (value) {
    if (this.enabled !== value) {
        this.enabled = value;
        this.fireEvent(new mxEventObject('stateChanged'));
    }
};

/**
 * Sets the enabled state of the action and fires a stateChanged event.
 */
Action.prototype.isEnabled = function () {
    return this.enabled;
};
